/**
 * <s-quick-add>
 * Panels on collection cards: the size picker (snippets/quick-add.liquid) and
 * the card details (snippets/card-details.liquid).
 * - [data-quick-add-toggle] opens/closes [data-quick-add-panel] (aria-expanded).
 * - Size picker: a [data-quick-add-size] chip selects its variant inside its own
 *   form (aria-pressed, id input); [data-quick-add-submit] then adds it through
 *   the enclosing s-product-form. Submitting without a size is blocked.
 * - A form may also live in a <dialog> (mobile bottom sheet); it works the same.
 * - Escape, a click outside, or a successful add closes the panel and any open sheet.
 */
class SQuickAdd extends HTMLElement {
  #ac = null;
  #toggle = null;
  #panel = null;
  #observers = [];
  #closeTimer = 0;

  connectedCallback() {
    this.#toggle = this.querySelector('[data-quick-add-toggle]');
    this.#panel = this.querySelector('[data-quick-add-panel]');
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Mobile sheets: open/close them directly (31 Oct 2026, taps on phones had no effect), and close on a
    // backdrop tap where closedby="any" is not supported. Native commands on an open dialog are a no-op.
    this.addEventListener('click', (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[commandfor]') : null;
      if (!trigger || !this.contains(trigger)) return;
      const dialog = document.getElementById(trigger.getAttribute('commandfor') || '');
      if (!(dialog instanceof HTMLDialogElement)) return;
      const command = trigger.getAttribute('command');
      if (command === 'show-modal' && !dialog.open) dialog.showModal();
      if (command === 'close' && dialog.open) dialog.close();
    }, { signal });
    this.querySelectorAll('dialog').forEach((dialog) => {
      this.#listenSwipeDown(dialog, signal);
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) dialog.close();
      }, { signal });
    });

    if (!this.#toggle || !this.#panel) return;

    this.#toggle.addEventListener('click', () => this.#setOpen(this.#panel.hidden), { signal });
    this.querySelectorAll("form").forEach((form) => this.#showStock(form, "in", 0));

    this.addEventListener('click', (event) => {
      const size = event.target instanceof Element ? event.target.closest('[data-quick-add-size]') : null;
      if (!size || size.disabled) return;
      this.#selectSize(size);
    }, { signal });

    // Never submit without a chosen size.
    this.addEventListener('submit', (event) => {
      const input = event.target instanceof HTMLFormElement ? event.target.elements.namedItem('id') : null;
      if (input instanceof HTMLInputElement && !input.value) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { signal, capture: true });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || this.#panel.hidden) return;
      event.stopPropagation();
      this.#setOpen(false);
      this.#toggle.focus();
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (!this.#panel.hidden && !this.contains(/** @type {Node} */ (event.target))) this.#setOpen(false);
    }, { signal });

    // Each sheet starts fresh, with a single available size preselected.
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('close', () => this.#resetForm(dialog.querySelector('form')), { signal });
      dialog.addEventListener('toggle', () => { if (dialog.open) this.#preselect(dialog); }, { signal });
    });

    // s-product-form sets aria-busy while adding and clears it when done.
    // The size form's button shows "adding", then briefly "added", before the panel/sheet closes.
    this.querySelectorAll('s-product-form').forEach((productForm) => {
      const submit = productForm.querySelector('[data-quick-add-submit]');
      const observer = new MutationObserver(() => {
        if (productForm.getAttribute('aria-busy') === 'true') {
          this.#setSubmitState(submit, 'adding');
          return;
        }
        const error = productForm.querySelector('[data-add-to-cart-error]');
        if (error && !error.hidden) {
          this.#setSubmitState(submit, 'idle');
          return;
        }
        const close = () => {
          this.#setOpen(false);
          this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
          this.#setSubmitState(submit, 'idle');
        };
        if (!submit) {
          close();
          return;
        }
        this.#setSubmitState(submit, 'added');
        clearTimeout(this.#closeTimer);
        this.#closeTimer = setTimeout(close, 900);
      });
      observer.observe(productForm, { attributes: true, attributeFilter: ['aria-busy'] });
      this.#observers.push(observer);
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#closeTimer);
    this.#ac?.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
  }

  #selectSize(size) {
    const form = size.form;
    if (!form) return;
    form.querySelectorAll('[data-quick-add-size]').forEach((chip) => chip.setAttribute('aria-pressed', String(chip === size)));
    const input = form.elements.namedItem('id');
    if (input instanceof HTMLInputElement) input.value = size.value;
    const submit = form.querySelector('[data-quick-add-submit]');
    if (submit) submit.disabled = false;
    const price = form.querySelector('[data-quick-add-price]');
    if (price) price.textContent = size.dataset.price ?? '';
    const chosen = form.querySelector('[data-quick-add-chosen]');
    if (chosen) chosen.textContent = size.dataset.size ?? ""; // stock count now shows in the Bestandsampel below
    this.#showStock(form, size.dataset.stock || "in", Number(size.dataset.count || 0));
  }

  /** Click feedback on the add button: idle → adding → added (labels in snippets/quick-add.liquid). */
  #setSubmitState(submit, state) {
    if (!submit) return;
    submit.dataset.qaState = state;
    let label = '';
    submit.querySelectorAll('[data-qa-label]').forEach((span) => {
      span.hidden = span.dataset.qaLabel !== state;
      if (!span.hidden) label = span.textContent.trim();
    });
    const live = submit.form?.querySelector('[data-qa-live]');
    if (live) live.textContent = state === 'idle' ? '' : label;
  }

  #resetForm(form) {
    if (!form || !form.querySelector('[data-quick-add-submit]')) return;
    form.querySelectorAll('[data-quick-add-size]').forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
    const input = form.elements.namedItem('id');
    if (input instanceof HTMLInputElement) input.value = '';
    form.querySelector('[data-quick-add-submit]').disabled = true;
    this.#setSubmitState(form.querySelector('[data-quick-add-submit]'), 'idle');
    const chosen = form.querySelector('[data-quick-add-chosen]');
    if (chosen) chosen.textContent = chosen.dataset.empty ?? '';
    this.#showStock(form, "in", 0);
  }

  /** Bestandsampel (pulsing dot) for the chosen size; before a choice the product shows as in stock. */
  #showStock(form, level, count) {
    const slot = form.querySelector("[data-quick-add-stock]");
    const tpl = this.querySelector("template[data-quick-add-stock-tpl]");
    if (!slot || !tpl) return;
    let scope = tpl.content;
    if (level === "low") scope = tpl.content.querySelector(count === 1 ? "[data-one]" : "[data-many]") || scope;
    const note = scope.querySelector(`[data-stock-level="${level}"]`);
    if (!note) {
      slot.replaceChildren();
      return;
    }
    const copy = note.cloneNode(true);
    if (level === "low") {
      const walker = document.createTreeWalker(copy, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) walker.currentNode.nodeValue = walker.currentNode.nodeValue.replace("987654", String(count));
    }
    slot.replaceChildren(copy);
  }

  #preselect(scope) {
    const available = [...scope.querySelectorAll('[data-quick-add-size]:not(:disabled)')];
    if (available.length === 1) this.#selectSize(available[0]);
  }

  /**
   * Bottom sheets follow the finger down and close past a threshold (or a quick flick); otherwise they snap back.
   * Only starts when the sheet's scrolled content is at its top, so scrolling inside the sheet still works.
   * @param {HTMLDialogElement} dialog
   * @param {AbortSignal} signal
   */
  #listenSwipeDown(dialog, signal) {
    let startY = 0;
    let startTime = 0;
    let dy = 0;
    let dragging = false;

    const reset = () => {
      dialog.style.transition = '';
      dialog.style.transform = '';
    };

    dialog.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1) return;
      const scroller = event.target instanceof Element ? event.target.closest('ul, [class*="overflow-y-auto"]') : null;
      if (scroller && scroller !== dialog && scroller.scrollTop > 0) return;
      startY = event.touches[0].clientY;
      startTime = performance.now();
      dy = 0;
      dragging = true;
    }, { passive: true, signal });

    dialog.addEventListener('touchmove', (event) => {
      if (!dragging) return;
      dy = Math.max(0, event.touches[0].clientY - startY);
      if (dy === 0) return;
      dialog.style.transition = 'none';
      dialog.style.transform = `translateY(${dy}px)`;
    }, { passive: true, signal });

    const end = () => {
      if (!dragging) return;
      dragging = false;
      const fast = dy > 40 && dy / Math.max(1, performance.now() - startTime) > 0.5;
      if (dy > 90 || fast) {
        dialog.style.transition = 'transform 0.22s cubic-bezier(0.2, 0.7, 0.2, 1)';
        dialog.style.transform = 'translateY(100%)';
        setTimeout(() => {
          dialog.close();
          reset();
        }, 220);
      } else if (dy > 0) {
        dialog.style.transition = 'transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1)';
        dialog.style.transform = '';
        setTimeout(reset, 300);
      }
    };
    dialog.addEventListener('touchend', end, { signal });
    dialog.addEventListener('touchcancel', end, { signal });
  }

  #setOpen(open) {
    this.#panel.hidden = !open;
    this.#toggle.setAttribute('aria-expanded', String(open));
    if (open) {
      this.#preselect(this.#panel);
      this.#panel.querySelector('[data-quick-add-size]:not(:disabled), button, a')?.focus({ preventScroll: true });
    } else {
      this.#panel.querySelectorAll('form').forEach((form) => this.#resetForm(form));
    }
  }
}

if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', SQuickAdd);
